import { computed, ref, watch } from 'vue'
import { useSearchInvoicesQuery } from '@/api/invoice'
import type { MultiselectOption } from '@ironsight/types'
import { BatchListItem } from '@/models/batch'
import { PaginatedDataProvider } from '@/models/types/paginated-data-provider'

const PAGE_LIMIT = 50

export function useBatchFilterDataProvider(): PaginatedDataProvider {
  const options = ref<MultiselectOption[]>([])
  const offset = ref(0)
  const currentQueryText = ref('')

  const queryGenerator = computed(() => {
    return {
      offset: offset.value,
      limit: PAGE_LIMIT,
      query: currentQueryText.value,
    }
  })

  const {
    invoices,
    isLoading: queryIsLoading,
    isFetching: queryIsFetching,
    error,
    hasMore,
  } = useSearchInvoicesQuery(queryGenerator)

  watch(
    invoices,
    (newBatches) => {
      if (newBatches) {
        const newOptions = newBatches.map((b: BatchListItem) => ({
          label: `#${b.invoiceNumber}`,
          value: b.id,
        }))
        const existingIds = new Set(options.value.map((item) => item.value))
        const uniqueNewOptions = newOptions.filter((opt) => !existingIds.has(opt.value))

        options.value.push(...uniqueNewOptions)
      }
    },
    { immediate: true },
  )

  function fetchNextPage(queryText: string = currentQueryText.value) {
    if (queryIsLoading.value && queryText === currentQueryText.value) {
      return
    }
    if (queryIsFetching.value && queryText === currentQueryText.value) {
      return
    }
    if (!hasMore.value && queryText === currentQueryText.value) {
      return
    }

    if (queryText !== currentQueryText.value) {
      currentQueryText.value = queryText
      offset.value = 0
      options.value = []
    }

    offset.value = options.value.length
  }

  return {
    options,
    isLoading: computed(() => queryIsLoading.value),
    isFetching: computed(() => queryIsFetching.value),
    hasMore: computed(() => hasMore.value),
    fetchNextPage,
  }
}
